import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { convertTimeToMinutes, convertTimeToSeconds, convertTimeWithSecondsToMinutes, getFloat, splitTime } from '../../../../helper'
import { handleTimeValidationOnBlur } from '../../../../helper/time'
import { updateCashDeskSettingsWaiterSettingsThunk } from '../../../../redux/thunk/cashDeskSettings'
import WaiterCartSettings from '../../../Elements/cartSettings/WaiterCartSettings'
import { CartSettingContent, CartSettingTitle } from '../index.styled'

const minWaiterLogoutTime = 3
const regexOnlyPositiveNumbers = /^(\d*)([.,]\d+)?$/

const CashDeskWaiterSettings = React.memo(() => {
	const waiterSettings = useSelector(store => store.cashDeskSettings.waiterSettings)
	const [waiterLogoutHours, setWaiterLogoutHours] = useState('')
	const [waiterLogoutMinutes, setWaiterLogoutMinutes] = useState('')
	const [waiterDiscount, setWaiterDiscount] = useState(0)
	const [showWaiterDiscountCheck, setShowWaiterDiscountCheck] = useState(false)
	const [timeError, setTimeError] = useState(false)
	const [discountError, setDiscountError] = useState(false)
	const dispatch = useDispatch()
	const { t } = useTranslation()

	useEffect(() => {
		if (waiterSettings) {
			const time = splitTime(waiterSettings.logoutTime)
			setWaiterLogoutHours(time?.hours)
			setWaiterLogoutMinutes(time?.minutes)
			setWaiterDiscount(waiterSettings.discount)
		}
	}, [waiterSettings])

	const onChangeWaiterLogoutTime = useCallback((event, type) => {
		const time = type === 'hours' ? `${event.target.value}:${waiterLogoutMinutes}` : `${waiterLogoutHours}:${event.target.value}`
		if (convertTimeWithSecondsToMinutes(waiterSettings.logoutTime) !== time) {
			const value = event.target.value
			const newTime = handleTimeValidationOnBlur(time)
			if (type === 'hours') {
				setWaiterLogoutHours(value)
			} else {
				setWaiterLogoutMinutes(value)
			}

			if (convertTimeToMinutes(newTime) >= minWaiterLogoutTime) {
				const updateWaiterSettings = { ...waiterSettings, logoutTime: convertTimeToSeconds(newTime) }
				dispatch(updateCashDeskSettingsWaiterSettingsThunk(updateWaiterSettings))
				setTimeError(false)
			} else {
				setTimeError(true)
			}
		}
	}, [waiterSettings, waiterLogoutMinutes, waiterLogoutHours, dispatch])

	const onChangeWaiterDiscount = useCallback((event) => {
		const value = event.target.value
		setWaiterDiscount(value)
		setShowWaiterDiscountCheck(false)
		setDiscountError(false)
	}, [])

	const handleValidateDiscount = useCallback(() => {
		if (waiterDiscount === '') return t('app.validation.required')
		if (!regexOnlyPositiveNumbers.test(waiterDiscount)) return t('Settings.WaiterCartSettings.error.positive_number')
		if (getFloat(waiterDiscount) > 100) return t('Settings.WaiterCartSettings.error.discount_error', { discount: '100%' })
	}, [waiterDiscount, t])

	const onBlurWaiterDiscount = useCallback(() => {
		if (waiterSettings.discount !== waiterDiscount) {
			const hasError = handleValidateDiscount()

			if (hasError) {
				setDiscountError(true)
			} else {
				const updateWaiterSettings = { ...waiterSettings, discount: getFloat(waiterDiscount) }
				dispatch(updateCashDeskSettingsWaiterSettingsThunk(updateWaiterSettings))
				setShowWaiterDiscountCheck(true)
			}
		}

	}, [waiterSettings, waiterDiscount, dispatch, handleValidateDiscount])

	return (
		<CartSettingContent>
			<CartSettingTitle>{t('Settings.WaiterCartSettings.waiterSettings')}</CartSettingTitle>
			<WaiterCartSettings {...{
				waiterLogoutHours, waiterLogoutMinutes, onChangeWaiterLogoutTime, minWaiterLogoutTime,
				timeError, waiterDiscount, onChangeWaiterDiscount, onBlurWaiterDiscount, showWaiterDiscountCheck,
				discountError: discountError && handleValidateDiscount(), className: 'lf-col-2 no-lf-border',
			}} />
		</CartSettingContent>
	)
})

CashDeskWaiterSettings.displayName = 'CashDeskWaiterSettings'
export default CashDeskWaiterSettings
